import { Translation, required } from '@ps/web-shared-components';
import React, { useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import useCurrencyFormatter from '@swf/core/hooks/useCurrencyFormatter';
import { select } from '@swf/core/store';

import type { WeeklyLimits } from '../../../../../../types/responsibleGamingLimits';
import { formatLimitOptions } from '../../../../../../utils/cashier';
import CurrencyLimit from '../../../../../CurrencyLimit/CurrencyLimit';
import InfoPrompt from '../../../../../InfoPrompt/InfoPrompt';

import {
  TXTWCL_7_Day_Poker_Buy_In_Limit,
  TXTWCL_7_Day_Sports_Buy_In_Limit,
  TXTWCL_How_does_accumulated_poker_buy_ELL,
  TXTWCL_How_does_accumulated_sports_be_ELL,
  TXTWCL_ItXs_a_rolling_limitX_so_think_ELL,
  TXTWCL_Once_you_reach_the_Poker_Buy_I_ELL,
  TXTWCL_Once_you_reach_the_Sports_Buy__ELL,
  TXTWCL_You_need_to_select_an_amount_t_ELL
} from './AccumulatedBuyInLimits.messages.json';
import { limitsWrapper } from './AccumulatedBuyInLimits.scss';

interface Props {
  fieldName: string;
  limits: WeeklyLimits;
}

const dropDownValues = [10000, 25000, 50000, 100000, 500000, 1000000, 5000000];

const getInitialValue = (limit: number) => (limit !== -1 ? limit : undefined);

const AccumulatedBuyInLimits: React.FC<Props> = ({
  fieldName,
  limits: { pokerLimit: initialPokerLimit, sportLimit: initialSportLimit }
}) => {
  const form = useForm();
  const formatAsCurrency = useCurrencyFormatter();
  const defaultCurrency = useSelector(select.account.getLimitCurrency);

  const options = formatLimitOptions(defaultCurrency, dropDownValues, formatAsCurrency);

  const {
    input: { value: pokerLimit }
  } = useField(`${fieldName}.pokerLimit`);
  const {
    input: { value: sportLimit }
  } = useField(`${fieldName}.sportLimit`);

  const pokerLimitChanged = pokerLimit !== initialPokerLimit;
  const sportLimitChanged = sportLimit !== initialSportLimit;

  useEffect(() => {
    form.change(`${fieldName}.pokerLimitChanged`, pokerLimitChanged);
    form.change(`${fieldName}.sportLimitChanged`, sportLimitChanged);
  }, [form, fieldName, pokerLimitChanged, sportLimitChanged]);

  return (
    <div className={limitsWrapper}>
      <CurrencyLimit
        id={`${fieldName}.pokerLimit`}
        label={TXTWCL_7_Day_Poker_Buy_In_Limit}
        options={options}
        initialFieldValue={getInitialValue(initialPokerLimit)}
        labelContent={
          <InfoPrompt
            title={{
              translation: TXTWCL_How_does_accumulated_poker_buy_ELL
            }}
          >
            <p>
              <Translation {...TXTWCL_Once_you_reach_the_Poker_Buy_I_ELL} />{' '}
              <Translation {...TXTWCL_ItXs_a_rolling_limitX_so_think_ELL} />
            </p>
          </InfoPrompt>
        }
        validators={[required(TXTWCL_You_need_to_select_an_amount_t_ELL)]}
      />
      <CurrencyLimit
        id={`${fieldName}.sportLimit`}
        label={TXTWCL_7_Day_Sports_Buy_In_Limit}
        options={options}
        initialFieldValue={getInitialValue(initialSportLimit)}
        labelContent={
          <InfoPrompt
            title={{
              translation: TXTWCL_How_does_accumulated_sports_be_ELL
            }}
          >
            <p>
              <Translation {...TXTWCL_Once_you_reach_the_Sports_Buy__ELL} />{' '}
              <Translation {...TXTWCL_ItXs_a_rolling_limitX_so_think_ELL} />
            </p>
          </InfoPrompt>
        }
        validators={[required(TXTWCL_You_need_to_select_an_amount_t_ELL)]}
      />
    </div>
  );
};

export default AccumulatedBuyInLimits;
